import { useApolloClient } from "@apollo/client"
import {
  ContextualFeaturePermission,
  ContextualFeaturePermissionsDocument,
  ContextualFeaturePermissionsQuery,
  ContextualFeaturePermissionsQueryVariables,
  CreateContextualFeaturePermissionMutationVariables,
  UpdateContextualFeaturePermissionMutationVariables,
  useCreateContextualFeaturePermissionMutation,
  useUpdateContextualFeaturePermissionMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

// Wrapper for conditionally updating or creating CFPs.
function useUpdateCFP(existingCFPId?: number | null) {
  const pwContext = usePathwrightContext()
  const client = useApolloClient()

  // Create or update depending on if CFP exists.
  const createMutation = useCreateContextualFeaturePermissionMutation()
  const updateMutation = useUpdateContextualFeaturePermissionMutation()
  const [mutation, mutationState] = existingCFPId
    ? updateMutation
    : createMutation

  async function updateCFP(
    variables:
      | UpdateContextualFeaturePermissionMutationVariables
      | CreateContextualFeaturePermissionMutationVariables
  ) {
    const cfp: ContextualFeaturePermission = {
      __typename: "ContextualFeaturePermission",
      id: existingCFPId || -1,
      feature_key: variables.featureKey,
      feature_action: variables.featureAction,
      permission_in_context: variables.permissionInContext,
      school_id: pwContext.school.id,
      resource_id: null,
      cohort_id: null,
      user_id: null,
      ...variables.context
    }

    // Attempt to update contextualFeaturePermissions query in cache, pushing this result.
    // TODO: while the data is found and succeessfully returning a new list, the useQuery hooks
    // using this data are not updating. Need to investigate further.
    // For now, disabled, since it results in poor UX.
    client.cache.updateQuery<
      ContextualFeaturePermissionsQuery,
      ContextualFeaturePermissionsQueryVariables
    >(
      {
        query: ContextualFeaturePermissionsDocument,
        variables: {
          featureAction: variables.featureAction,
          featureKeys: { in: [variables.featureKey] },
          context: variables.context
        }
      },
      (data): ContextualFeaturePermissionsQuery | null => {
        return data
        if (!data) return null

        const existingCFPs = data?.contextualFeaturePermissions || []

        // If updating, replace existing CFP, else add new CFP.
        const updatedCFPs = existingCFPId
          ? existingCFPs.map((existingCFP) =>
              existingCFP.id === existingCFPId ? cfp : existingCFP
            )
          : [...existingCFPs, cfp]

        return {
          ...data,
          contextualFeaturePermissions: updatedCFPs
        }
      }
    )

    const promise = mutation({
      variables,
      optimisticResponse: {
        __typename: "Mutations",
        [existingCFPId
          ? "updateContextualFeaturePermission"
          : "createContextualFeaturePermission"]: cfp
      }
    })

    if (!existingCFPId) {
      // If creating, wait for server response to get new ID/data.
      await promise
    }
  }

  return [updateCFP, mutationState] as const
}

export default useUpdateCFP
